import React, { useState, useEffect, useContext, useRef } from "react";
import { Box } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import PropTypes from "prop-types";
import AuthContext from "connections/auth/AuthProvider.js";
import ChannelTransparencyTable from "./ChannelTransparencyTable";
import { getChannelTransparencyData } from "../../viewServices/apiCalls";
import {
  CardLoadingState,
  CardErrorState,
  CardEmptyState,
  CardNoDataState,
} from "../shared";

const MIN_HEIGHT = 180;

const useStyles = makeStyles(() => ({
  contentWrapper: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    height: "100%",
  },
}));

export default function CardChannelTransparency({
  programId,
  filters,
  viewMode = "client",
}) {
  const classes = useStyles();
  const { userDetails } = useContext(AuthContext);
  const clientColor = userDetails?.implementation_color || "#1976d2";

  // Estado de drill-down
  const [drillDownsOpen, setDrillDownsOpen] = useState([]);
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Ref para rastrear o viewMode anterior e limpar drill-downs ao mudar
  const prevViewModeRef = useRef(viewMode);

  // Limpar drill-downs quando viewMode mudar
  useEffect(() => {
    if (prevViewModeRef.current !== viewMode) {
      setDrillDownsOpen([]);
      prevViewModeRef.current = viewMode;
    }
  }, [viewMode]);

  useEffect(() => {
    const fetchData = async () => {
      if (!programId) {
        setIsLoading(false);
        setData(null);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        // Fetch data by program - returns data_by_type (actual/forecast/policy)
        const response = await getChannelTransparencyData(programId, filters);
        const dataByType = response.data_by_type || {};

        // Use actual data for now (can be extended to show comparison)
        const actualData = dataByType.actual;

        // Response should have { columns: [...], rows: [...] }
        if (actualData && actualData.columns && actualData.rows) {
          setData(actualData);
        } else {
          console.warn("Unexpected response format:", response);
          setData(null);
        }
      } catch (err) {
        console.error("Error fetching Channel Transparency data:", err);
        setError(err.message || "Failed to load Channel Transparency data");
        setData(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [programId, filters]);

  // Loading state
  if (isLoading) {
    return <CardLoadingState minHeight={MIN_HEIGHT} />;
  }

  // Error state
  if (error) {
    return <CardErrorState message={error} minHeight={MIN_HEIGHT} />;
  }

  // Empty state (no program selected)
  if (!programId) {
    return <CardEmptyState minHeight={MIN_HEIGHT} />;
  }

  // No data state
  if (!data || !data.rows || data.rows.length === 0) {
    return (
      <CardNoDataState
        title="Nenhum dado de canal disponível"
        subtitle="Verifique os scenarios do program"
        minHeight={MIN_HEIGHT}
      />
    );
  }

  return (
    <Box className={classes.contentWrapper}>
      <ChannelTransparencyTable
        data={data}
        drillDownsOpen={drillDownsOpen}
        setDrillDownsOpen={setDrillDownsOpen}
        clientColor={clientColor}
        viewMode={viewMode}
      />
    </Box>
  );
}

CardChannelTransparency.propTypes = {
  programId: PropTypes.number,
  filters: PropTypes.object,
  viewMode: PropTypes.oneOf(["channel", "client"]),
};
